/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { columns as datacenterConnectionColumns, DatacenterConnection } from "./DatacenterConnectionsData";
import { DatacenterConnectionsViewModal } from "./Modals/DatacenterConnectionsViewModal";
import { DatacenterConnectionsEditModal } from "./Modals/DatacenterConnectionsEditModal";
import { DatacenterConnectionsCreateModal } from "./Modals/DatacenterConnectionsCreateModal";

import { ReactComponent as IconEye } from "../../../../shared/assets/Icons/Icon_Eye.svg";
import { ReactComponent as IconPlus } from "../../../../shared/assets/Icons/IconPlusNV.svg";
import { ReactComponent as IconEdit } from "../../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../../shared/assets/Icons/Icon_Delete.svg";
import {
  getAllDatacenterConnections,
  getDatacenterConnectionById,
  createDatacenterConnection,
  updateDatacenterConnection,
  deleteDatacenterConnection,
} from "./request/request";
import { useAppSelector } from "@redux/hooks";
import { ParsedFilter as OldParsed, FilterField, buildPredicate, labelId, parseToken } from "../../../helpers/advancedFilters";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";

export default function DatacenterConnectionsPage() {
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();

  // 1) State for table data
  const [connections, setConnections] = useState<DatacenterConnection[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  // 2) Delete confirmation
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState<DatacenterConnection | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [activeFilters, setActiveFilters] = useState<OldParsed[]>([]);
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;

  // Rows per page state for UI table pagination (default 10)
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // 3) View modal state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState<DatacenterConnection | null>(null);

  // 4) Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [connectionToEdit, setConnectionToEdit] = useState<DatacenterConnection | null>(null);

  // 5) Create modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Fetch connections on mount
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    (async () => {
      try {
        setIsFetching(true);
        const token = await getAccessTokenSilently();
        const resp = await getAllDatacenterConnections(token);
        // Handle both direct array response and nested { data: [...] } response
        const connectionsData = Array.isArray(resp.data) 
          ? resp.data 
          : resp.data?.data || [];
        
        setConnections(connectionsData);
      } catch (err) {
        console.error("Error fetching datacenter connections:", err);
        setConnections([]);
      } finally {
        setIsFetching(false);
      }
    })();
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  // ------------- Handler: Add -------------
  function handleAddConnection() {
    setIsCreateModalOpen(true);
  }

  // ------------- Handler: Create Connection -------------
  const handleCreateConnection = async (newData: Partial<DatacenterConnection>) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await createDatacenterConnection(token, newData as any);
      
      // Check if response indicates success (201 Created or object with id)
      const isSuccess = response.status === 201 || 
      (response.data && response.data.id !== undefined) ||
      (response.data?.success === true) ||
      (response.data?.data && response.data.data.id !== undefined);

      if (isSuccess) {
        // Close modal first
        setIsCreateModalOpen(false);
        
        // Show skeleton and refetch all connections
        setIsFetching(true);
        try {
          const resp = await getAllDatacenterConnections(token);
          const connectionsData = Array.isArray(resp.data) 
            ? resp.data 
            : resp.data?.data || [];
          
          setConnections(connectionsData);
        } catch (refetchError) {
          console.error("Error refetching connections after creation:", refetchError);
        } finally {
          setIsFetching(false);
        }
      } else {
        throw new Error(response.data?.message || "Failed to create connection");
      }
    } catch (error: any) {
      console.error("Error creating connection:", error);
      const errorMessage = error?.response?.data?.message 
        || error?.message 
        || "An error occurred while creating the connection";
      throw new Error(errorMessage);
    }
  };

  const handleViewConnection = async (connection: DatacenterConnection) => {
    setIsViewModalOpen(true);
    setIsViewLoading(true);
    setSelectedConnection(null);
    
    try {
      const token = await getAccessTokenSilently();
      const response = await getDatacenterConnectionById(token, connection.id);
      const connectionData = response.data?.data || response.data;
      setSelectedConnection(connectionData as DatacenterConnection);
    } catch (error) {
      console.error("Error viewing connection:", error);
      setSelectedConnection(connection);
    } finally {
      setIsViewLoading(false);
    }
  };

  // ------------- Handler: Edit -------------
  const handleEditConnection = async (connection: DatacenterConnection) => {
    setIsEditModalOpen(true);
    setIsEditLoading(true);
    setConnectionToEdit(null);
    
    try {
      const token = await getAccessTokenSilently();
      const response = await getDatacenterConnectionById(token, connection.id);
      const connectionData = response.data?.data || response.data;
      setConnectionToEdit(connectionData as DatacenterConnection);
    } catch (error) {
      console.error("Error loading connection for edit:", error);
      setConnectionToEdit(connection);
    } finally {
      setIsEditLoading(false);
    }
  };

  // ------------- Handler: Save Connection -------------
  const handleSaveConnection = async (updatedData: Partial<DatacenterConnection>) => {
    if (!connectionToEdit) return;
    
    try {
      const token = await getAccessTokenSilently();
      const response = await updateDatacenterConnection(token, connectionToEdit.id, updatedData as any);
      
      // Check if response indicates success
      if (response.data?.success === true || response.status === 200) {
        // Close modal first
        setIsEditModalOpen(false);
        
        // Show skeleton and refetch all connections
        setIsFetching(true);
        try {
          const resp = await getAllDatacenterConnections(token);
          const connectionsData = Array.isArray(resp.data) 
            ? resp.data 
            : resp.data?.data || [];
          
          setConnections(connectionsData);
        } catch (refetchError) {
          console.error("Error refetching connections after update:", refetchError);
        } finally {
          setIsFetching(false);
        }
      } else {
        throw new Error(response.data?.message || "Failed to update connection");
      }
    } catch (error: any) {
      console.error("Error saving connection:", error);
      const errorMessage = error?.response?.data?.message 
        || error?.message 
        || "An error occurred while updating the connection";
      throw new Error(errorMessage);
    }
  };

  // ------------- Handler: Delete -------------
  const handleDeleteConnection = (connection: DatacenterConnection) => {
    setConnectionToDelete(connection);
    setIsAlertOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!connectionToDelete) return;

    try {
      setIsDeleting(true);
      const token = await getAccessTokenSilently();
      const response = await deleteDatacenterConnection(token, connectionToDelete.id);
      
      // Check if response indicates success
      if (response.data?.success === true || response.status === 200 || response.status === 204) {
        // Close the alert first
        setIsAlertOpen(false);
        setConnectionToDelete(null);
        
        // Show skeleton and refetch all connections
        setIsFetching(true);
        try {
          const resp = await getAllDatacenterConnections(token);
          const connectionsData = Array.isArray(resp.data) 
            ? resp.data 
            : resp.data?.data || [];
          
          setConnections(connectionsData);
        } catch (refetchError) {
          console.error("Error refetching connections after delete:", refetchError);
        } finally {
          setIsFetching(false);
        }
      } else {
        throw new Error(response.data?.message || "Failed to delete connection");
      }
    } catch (error: any) {
      console.error("Error deleting connection:", error);
      const errorMessage = error?.response?.data?.message 
        || error?.message 
        || "An error occurred while deleting the connection";
      alert(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setConnectionToDelete(null);
    setIsAlertOpen(false);
  };

  // ------------------------------------------------------------------------------------
  // Filter fields for GenericSearchbar
  // ------------------------------------------------------------------------------------
  const connectionFields: FilterField<DatacenterConnection>[] = useMemo(() => [
    { label: "ID", accessor: (row) => String(row.id), type: "string" },
    { label: "Subscription ID", accessor: (row) => row.subId, type: "string" },
    { label: "Source Datacenter", accessor: (row) => row.datacenterIdSource, type: "string" },
    { label: "Destination Datacenter", accessor: (row) => String(row.datacenterIdDest || ""), type: "string" },
    { label: "Source Hardware", accessor: (row) => row.hardwareSource?.hostname || "", type: "string" },
    { label: "Destination Hardware", accessor: (row) => row.hardwareDest?.hostname || "", type: "string" },
    { label: "Source Port", accessor: (row) => row.portSource?.portNumber || "", type: "string" },
    { label: "Destination Port", accessor: (row) => row.portDest?.portNumber || "", type: "string" },
    { label: "Capacity (Mbps)", accessor: (row) => String(row.capacityMbps), type: "number" },
    { label: "Connection Type", accessor: (row) => row.connectionType, type: "string" },
  ], []);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of connectionFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [connectionFields]);

  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<DatacenterConnection>>();
    for (const f of connectionFields) {
      const id = f.key ?? labelId(f.label);
      m.set(id.toLowerCase(), f);
    }
    return m;
  }, [connectionFields]);

  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      const token = f.op ? { ...f } : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string" && token.value) {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else if (!token.op) {
          token.op = ":"; // contains
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  const predicate = useMemo(
    () => buildPredicate<DatacenterConnection>(normalizedFilters, connectionFields),
    [normalizedFilters, connectionFields]
  );

  const filteredConnections = useMemo(
    () => connections.filter(predicate),
    [connections, predicate]
  );

  // ------------------------------------------------------------------------------------
  // Override "Actions" column
  // ------------------------------------------------------------------------------------
  const columns = datacenterConnectionColumns.map((col) => {
    if (col.header === "Actions" && col.renderCell) {
      return {
        ...col,
        renderCell: (row: DatacenterConnection) => (
          <div className="flex items-center gap-2">
            {/* View */}
            <button
              className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
              onClick={() => handleViewConnection(row)}
            >
              <IconEye className="w-6 h-6" />
            </button>

            {/* Edit */}
            <button
              className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
              onClick={() => handleEditConnection(row)}
            >
              <IconEdit className="w-10 h-10" />
            </button>

            {/* Delete */}
            <button
              className="
                p-2
                border border-semantic-border-secondary
                rounded
                bg-red-50
                text-red-600
                hover:bg-red-100
              "
              onClick={() => handleDeleteConnection(row)}
            >
              <IconDelete className="w-6 h-6" />
            </button>
          </div>
        ),
      };
    }
    return col;
  });

  const suggestedKeys = useMemo(() => {
    const headers = datacenterConnectionColumns
      .map((c) => c.header)
      .filter(Boolean) as string[];

    if (isViewingAllCustomers && !headers.includes("Customer")) {
      headers.splice(1, 0, "Customer");
    }
    return headers;
  }, [datacenterConnectionColumns, isViewingAllCustomers]);

  return (
    <div className="relative p-1 sm:p-4 overflow-hidden flex flex-col h-full">
      {/* Card con tabla */}
      <div className="relative flex-1 min-h-0">
        <GenericCard
          isCollapsible={false}
          className="h-full flex flex-col"
          bodyContent={
            <div className="flex flex-col h-full min-h-0">
              {/* Searchbar y botón */}
              <div className="mb-2 sm:mb-4 bg-[var(--semantic-bg-primary)] flex-shrink-0">
                <div className="flex items-center justify-between gap-4 mt-4 mb-10">
                  <div className="w-[520px]">
                    <GenericSearchbar
                      placeholder="Find by attribute or value"
                      suggestedKeys={suggestedKeys}
                      onFiltersChange={setActiveFilters}
                    />
                  </div>
                  <GenericButton
                    label={
                      <div className="flex items-center gap-2">
                        <IconPlus className="w-4 h-4" />
                        <span>Add Connection</span>
                      </div>
                    }
                    variant="brand"
                    size="md"
                    onClick={handleAddConnection}
                  />
                </div>
              </div>

              {/* Table wrapper */}
              <div className="flex-1 min-h-0 flex flex-col">
                {isFetching ? (
                  <TableSkeleton columns={5} rows={5} />
                ) : (
                  <GenericTable
                    columns={columns}
                    data={filteredConnections}
                    initialRowsPerPage={10}
                    rowsPerPageOptions={[10, 20, 30]}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={setRowsPerPage}
                    width="100%"
                    maxTableBodyHeight="100%"
                  />
                )}
              </div>
            </div>
          }
        />
      </div>

      {/* View Connection Modal */}
      <DatacenterConnectionsViewModal
        isOpen={isViewModalOpen}
        isLoading={isViewLoading}
        connection={selectedConnection}
        onClose={() => setIsViewModalOpen(false)}
      />

      {/* Edit Connection Modal */}
      <DatacenterConnectionsEditModal
        isOpen={isEditModalOpen}
        isLoading={isEditLoading}
        connection={connectionToEdit}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveConnection}
      />

      {/* Create Connection Modal */}
      <DatacenterConnectionsCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateConnection}
      />

      {/* Delete Confirmation */}
      <GenericAlert
        isOpen={isAlertOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Are you sure?"
        description="This action cannot be undone. This will permanently delete the connection."
        cancelLabel="Cancel"
        confirmLabel={isDeleting ? "Deleting..." : "Delete"}
        confirmButtonDisabled={isDeleting}
      />
    </div>
  );
}
